import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/contexts/AuthContext";
import { db, storage } from "@/lib/firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadString, getDownloadURL } from "firebase/storage";
import { loiSchema, type LOIFormData, defaultConditions, defaultAssetsIncluded } from "@/schemas/loiSchema";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { FileText, Download, Plus, X, FileDown } from "lucide-react";
import { generatePdfFromHtml } from "@/utils/pdf-generator";
import SignaturePad from "@/components/tools/SignaturePad";
import { Separator } from "@/components/ui/separator";
import { useLocation, useNavigate } from "react-router-dom";
import { collection as fsCollection, getDocs, query, orderBy } from "firebase/firestore";
import { ArrowLeft, Save, FolderOpen, Database } from "lucide-react";

type DealOption = { id: string; reference: string; businessName: string };

export default function LOIGenerator() {
  const { user } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatedLOI, setGeneratedLOI] = useState<string | null>(null);
  const [customCondition, setCustomCondition] = useState("");
  const [dealReference, setDealReference] = useState<string | null>(null);
  const [deals, setDeals] = useState<DealOption[]>([]);
  const [loadingDeals, setLoadingDeals] = useState(true);
  const [selectedDealId, setSelectedDealId] = useState<string>("");
  // Document output language — defaults to English regardless of UI language (legal docs policy).
  const [docLang, setDocLang] = useState<"en" | "fr">("en");

  const isAdminOrStaff = user?.roles?.includes("admin") || user?.roles?.includes("staff");

  // Load submitted deals so staff can pre-select context
  useEffect(() => {
    (async () => {
      setLoadingDeals(true);
      try {
        const snap = await getDocs(query(fsCollection(db, "deals"), orderBy("createdAt", "desc")));
        setDeals(snap.docs.map((d) => {
          const v = d.data() as any;
          return { id: d.id, reference: v.referenceNumber || "", businessName: v.businessName || "" };
        }));
      } catch (e) { console.error("Failed to load deals", e); }
      finally { setLoadingDeals(false); }
    })();
  }, []);

  const handleDealPick = async (id: string) => {
    setSelectedDealId(id);
    const d = deals.find((x) => x.id === id);
    if (!d) return;
    if (d.reference) setDealReference(d.reference);
    if (d.businessName) form.setValue("businessName", d.businessName);
    toast({ title: "Deal selected", description: d.reference || d.businessName });
  };

  const draftKey = () => `loi_draft_${selectedDealId || dealReference || "unsaved"}`;

  const handleSaveDraft = () => {
    try {
      const payload = { values: form.getValues(), dealReference, selectedDealId, savedAt: new Date().toISOString() };
      localStorage.setItem(draftKey(), JSON.stringify(payload));
      localStorage.setItem("loi_draft_last", draftKey());
      toast({ title: "Draft saved", description: "Saved locally for this deal" });
    } catch { toast({ title: "Save failed", variant: "destructive" }); }
  };

  const handleLoadDraft = () => {
    try {
      const key = draftKey();
      const raw = localStorage.getItem(key) || (localStorage.getItem("loi_draft_last") && localStorage.getItem(localStorage.getItem("loi_draft_last")!));
      if (!raw) { toast({ title: "No draft found", variant: "destructive" }); return; }
      const p = JSON.parse(raw);
      if (p.values) form.reset(p.values);
      if (p.dealReference) setDealReference(p.dealReference);
      if (p.selectedDealId) setSelectedDealId(p.selectedDealId);
      toast({ title: "Draft loaded" });
    } catch { toast({ title: "Load failed", variant: "destructive" }); }
  };

  // Fetch deal reference_number if dealId is provided via router state
  useEffect(() => {
    const dealId = (location.state as any)?.dealId;
    if (!dealId) return;
    (async () => {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const snap = await getDoc(doc(db, 'deals', dealId));
        const data: any = snap.exists() ? snap.data() : null;
        const ref = data?.referenceNumber;
        if (ref) setDealReference(ref);
      } catch (err) {
        console.error('Failed to fetch deal reference:', err);
      }
    })();
  }, [location.state]);

  const form = useForm<LOIFormData>({
    resolver: zodResolver(loiSchema),
    defaultValues: {
      buyer: { name: "", company: "", address: "", city: "", email: "", phone: "", signature: "" },
      seller: { name: "", company: "", address: "", city: "", email: "", phone: "", signature: "" },
      businessName: "",
      businessDescription: "",
      businessAddress: "",
      purchasePrice: 0,
      currency: "USD",
      depositAmount: 0,
      paymentStructure: {
        cashAtClosing: 0,
        sellerFinancing: 0,
        earnout: 0,
        other: 0,
        description: "",
      },
      dueDiligenceDays: 60,
      closingDays: 90,
      exclusivityDays: 90,
      conditions: defaultConditions.slice(0, 5),
      assetsIncluded: defaultAssetsIncluded.slice(0, 6),
      assetsExcluded: [],
      employeeRetention: true,
      sellerNonCompete: true,
      nonCompeteYears: 3,
      nonCompeteRadius: "50 miles",
      sellerTransitionPeriod: 3,
      applicableLaw: "Florida, USA",
      arbitration: true,
      confidential: true,
      bindingIntent: false,
      expirationDays: 14,
    },
  });

  const generateLOIDocument = (data: LOIFormData, docRef?: string | null): string => {
    const today = new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    const formatCurrency = (amount: number) => 
      new Intl.NumberFormat('en-US', { style: 'currency', currency: data.currency }).format(amount);

    return `
<!DOCTYPE html>
<html>
<head>
  <title>Letter of Intent - ${data.businessName}</title>
  <style>
    body { font-family: 'Georgia', serif; max-width: 850px; margin: 0 auto; padding: 40px; line-height: 1.8; color: #333; }
    h1 { text-align: center; color: #1a365d; border-bottom: 2px solid #1a365d; padding-bottom: 15px; }
    h2 { color: #2c5282; margin-top: 30px; font-size: 1.2em; }
    .header-info { text-align: center; margin-bottom: 30px; }
    .party-section { display: flex; gap: 40px; margin: 20px 0; }
    .party { flex: 1; padding: 20px; background: #f7fafc; border-radius: 8px; border-left: 4px solid #3182ce; }
    .party h3 { margin: 0 0 15px 0; color: #2c5282; }
    .terms-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
    .terms-table td { padding: 10px; border-bottom: 1px solid #e2e8f0; }
    .terms-table td:first-child { font-weight: bold; width: 40%; }
    .conditions-list { list-style-type: none; padding: 0; }
    .conditions-list li { padding: 8px 0 8px 25px; position: relative; }
    .conditions-list li:before { content: "✓"; position: absolute; left: 0; color: #38a169; }
    .signature-section { margin-top: 50px; page-break-inside: avoid; }
    .signature-box { display: inline-block; width: 45%; vertical-align: top; margin: 20px 2%; }
    .signature-line { border-bottom: 1px solid #333; margin: 10px 0; min-height: 60px; }
    .signature-img { max-width: 250px; max-height: 80px; }
    .legal-notice { background: #fff5f5; padding: 15px; border-radius: 8px; margin: 30px 0; border: 1px solid #fc8181; }
    .confidential-banner { background: #1a365d; color: white; padding: 10px; text-align: center; margin-bottom: 30px; }
    .amount-highlight { font-size: 1.3em; color: #2c5282; font-weight: bold; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  ${data.confidential ? '<div class="confidential-banner">⚠️ CONFIDENTIAL DOCUMENT</div>' : ''}
  
  <h1>LETTER OF INTENT</h1>
  <div class="header-info">
    ${docRef ? `<p style="font-size: 0.9em; color: #4a5568; font-weight: bold;">Ref: ${docRef}</p>` : ''}
    <p>Date: ${today}</p>
    <p>Re: Proposed Acquisition of <strong>${data.businessName}</strong></p>
  </div>

  <h2>1. PARTIES</h2>
  <div class="party-section">
    <div class="party">
      <h3>BUYER</h3>
      <p><strong>${data.buyer.name}</strong></p>
      <p>${data.buyer.company}</p>
      <p>${data.buyer.address}<br>${data.buyer.city}</p>
      <p>Email: ${data.buyer.email}<br>Phone: ${data.buyer.phone}</p>
    </div>
    <div class="party">
      <h3>SELLER</h3>
      <p><strong>${data.seller.name}</strong></p>
      <p>${data.seller.company}</p>
      <p>${data.seller.address}<br>${data.seller.city}</p>
      <p>Email: ${data.seller.email}<br>Phone: ${data.seller.phone}</p>
    </div>
  </div>

  <h2>2. TARGET BUSINESS</h2>
  <table class="terms-table">
    <tr><td>Business Name</td><td>${data.businessName}</td></tr>
    <tr><td>Address</td><td>${data.businessAddress}</td></tr>
    <tr><td>Description</td><td>${data.businessDescription}</td></tr>
  </table>

  <h2>3. PURCHASE PRICE</h2>
  <p>The proposed purchase price is: <span class="amount-highlight">${formatCurrency(data.purchasePrice)}</span></p>
  <table class="terms-table">
    <tr><td>Deposit/Escrow</td><td>${formatCurrency(data.depositAmount)}</td></tr>
    <tr><td>Cash at Closing</td><td>${formatCurrency(data.paymentStructure.cashAtClosing)}</td></tr>
    <tr><td>Seller Financing</td><td>${formatCurrency(data.paymentStructure.sellerFinancing)}</td></tr>
    <tr><td>Earnout</td><td>${formatCurrency(data.paymentStructure.earnout)}</td></tr>
    ${data.paymentStructure.other > 0 ? `<tr><td>Other</td><td>${formatCurrency(data.paymentStructure.other)}</td></tr>` : ''}
  </table>
  ${data.paymentStructure.description ? `<p><em>Payment Notes: ${data.paymentStructure.description}</em></p>` : ''}

  <h2>4. TIMELINE</h2>
  <table class="terms-table">
    <tr><td>Due Diligence Period</td><td>${data.dueDiligenceDays} days from acceptance</td></tr>
    <tr><td>Target Closing Date</td><td>${data.closingDays} days from acceptance</td></tr>
    <tr><td>Exclusivity Period</td><td>${data.exclusivityDays} days</td></tr>
    <tr><td>LOI Expiration</td><td>${data.expirationDays} days from date above</td></tr>
  </table>

  <h2>5. CONDITIONS PRECEDENT</h2>
  <p>This transaction is subject to the following conditions:</p>
  <ul class="conditions-list">
    ${data.conditions.map(c => `<li>${c}</li>`).join('')}
  </ul>

  <h2>6. ASSETS INCLUDED</h2>
  <ul class="conditions-list">
    ${data.assetsIncluded.map(a => `<li>${a}</li>`).join('')}
  </ul>
  ${data.assetsExcluded && data.assetsExcluded.length > 0 ? `
  <h3>Assets Excluded:</h3>
  <ul>${data.assetsExcluded.map(a => `<li>${a}</li>`).join('')}</ul>
  ` : ''}

  <h2>7. ADDITIONAL TERMS</h2>
  <table class="terms-table">
    <tr><td>Employee Retention</td><td>${data.employeeRetention ? 'Buyer intends to offer employment to existing employees' : 'No commitment to retain employees'}</td></tr>
    <tr><td>Non-Compete Agreement</td><td>${data.sellerNonCompete ? `Seller agrees to ${data.nonCompeteYears} year non-compete within ${data.nonCompeteRadius}` : 'None required'}</td></tr>
    <tr><td>Transition Period</td><td>${data.sellerTransitionPeriod ? `${data.sellerTransitionPeriod} months consulting/transition period` : 'None'}</td></tr>
    <tr><td>Governing Law</td><td>${data.applicableLaw}</td></tr>
    <tr><td>Dispute Resolution</td><td>${data.arbitration ? 'Binding arbitration' : 'Court jurisdiction'}</td></tr>
  </table>

  <div class="legal-notice">
    <strong>${data.bindingIntent ? '⚖️ BINDING DOCUMENT' : '📋 NON-BINDING LETTER OF INTENT'}</strong>
    <p>${data.bindingIntent 
      ? 'This Letter of Intent creates legally binding obligations upon execution by both parties.' 
      : 'This Letter of Intent is intended to express the mutual intent of the parties but does not create any binding legal obligations, except for the provisions regarding confidentiality and exclusivity which shall be binding upon execution.'
    }</p>
  </div>

  <div class="signature-section">
    <h2>8. SIGNATURES</h2>
    <p>IN WITNESS WHEREOF, the parties have executed this Letter of Intent as of the date first written above.</p>
    
    <div class="signature-box">
      <p><strong>BUYER:</strong></p>
      <div class="signature-line">
        ${data.buyer.signature ? `<img src="${data.buyer.signature}" alt="Buyer Signature" class="signature-img" />` : ''}
      </div>
      <p>Name: ${data.buyer.name}</p>
      <p>Title: Prospective Buyer</p>
      <p>Date: ${today}</p>
    </div>
    
    <div class="signature-box">
      <p><strong>SELLER:</strong></p>
      <div class="signature-line">
        ${data.seller.signature ? `<img src="${data.seller.signature}" alt="Seller Signature" class="signature-img" />` : ''}
      </div>
      <p>Name: ${data.seller.name}</p>
      <p>Title: Owner/Authorized Representative</p>
      <p>Date: _______________</p>
    </div>
  </div>
  ${docRef ? `<div style="margin-top: 40px; padding-top: 10px; border-top: 1px solid #e2e8f0; text-align: center; font-size: 0.8em; color: #718096;">Document Ref: ${docRef}</div>` : ''}
</body>
</html>`;
  };

  const onSubmit = async (data: LOIFormData) => {
    if (!user) {
      toast({ title: "Authentication Required", description: "Please log in to generate LOI", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      const loiHtml = generateLOIDocument(data, dealReference);
      
      // Save HTML to Firebase Storage
      const storageRef = ref(storage, `lois/${user.id}/${Date.now()}_loi.html`);
      await uploadString(storageRef, loiHtml, "raw", { contentType: "text/html" });
      const loiUrl = await getDownloadURL(storageRef);

      // Save LOI record to Firestore
      await addDoc(collection(db, "lois"), {
        userId: user.id,
        buyerName: data.buyer.name,
        buyerCompany: data.buyer.company,
        sellerName: data.seller.name,
        sellerCompany: data.seller.company,
        businessName: data.businessName,
        purchasePrice: data.purchasePrice,
        currency: data.currency,
        documentUrl: loiUrl,
        status: data.buyer.signature && data.seller.signature ? "signed" : "draft",
        createdAt: serverTimestamp(),
      });

      setGeneratedLOI(loiHtml);
      toast({ title: "Success", description: "Letter of Intent generated successfully" });
    } catch (error) {
      console.error("Error generating LOI:", error);
      toast({ title: "Error", description: "Failed to generate LOI", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const downloadLOI = () => {
    if (!generatedLOI) return;
    const blob = new Blob([generatedLOI], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `LOI-${form.getValues('businessName').replace(/\s+/g, '-')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadLOIasPdf = async () => {
    if (!generatedLOI) return;
    
    try {
      await generatePdfFromHtml(generatedLOI, {
        filename: `LOI-${form.getValues('businessName').replace(/\s+/g, '-')}.pdf`,
        pageSize: 'letter',
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast({ title: "Error", description: "Failed to generate PDF", variant: "destructive" });
    }
  };

  const addCustomCondition = () => {
    if (!customCondition.trim()) return;
    const current = form.getValues('conditions');
    form.setValue('conditions', [...current, customCondition.trim()]);
    setCustomCondition("");
  };

  const removeCondition = (index: number) => {
    const current = form.getValues('conditions');
    form.setValue('conditions', current.filter((_, i) => i !== index));
  };

  const watchedConditions = form.watch('conditions');
  const watchedAssets = form.watch('assetsIncluded');

  if (generatedLOI) {
    return (
      <DashboardLayout>
        <div className="space-y-6 max-w-4xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Letter of Intent Generated
              </CardTitle>
              <CardDescription>Your LOI has been created and saved</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border rounded-lg p-4 max-h-[600px] overflow-y-auto bg-white" dangerouslySetInnerHTML={{ __html: generatedLOI }} />
              <div className="flex flex-wrap gap-2">
                <Button onClick={downloadLOIasPdf}><FileDown className="mr-2 h-4 w-4" />Download PDF</Button>
                <Button variant="outline" onClick={downloadLOI}><Download className="mr-2 h-4 w-4" />Download HTML</Button>
                <Button variant="outline" onClick={() => setGeneratedLOI(null)}>Create Another</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Sticky action bar: Back / Deal selector / Save / Load (admin & staff) */}
        <div className="sticky top-0 z-30 -mx-2 px-2 py-3 bg-background/95 backdrop-blur border-b flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(isAdminOrStaff ? (user?.roles?.includes("admin") ? "/admin/overview" : "/staff") : "/dashboard")}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to dashboard
          </Button>
          {isAdminOrStaff && (
            <div className="flex items-center gap-2 ml-2">
              <Database className="w-4 h-4 text-amber-600" />
              <select
                value={selectedDealId}
                onChange={(e) => handleDealPick(e.target.value)}
                disabled={loadingDeals}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm min-w-[260px]"
              >
                <option value="">{loadingDeals ? "Loading deals…" : "— Select a submitted deal —"}</option>
                {deals.map((d) => (
                  <option key={d.id} value={d.id}>
                    {(d.reference || `(${d.id.slice(0, 6)}…)`) + " — " + (d.businessName || "—")}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="ml-auto flex gap-2 items-center">
            <div className="flex items-center gap-1 mr-1 border rounded-md p-0.5" title="Document output language (defaults to English)">
              <button
                type="button"
                onClick={() => setDocLang("en")}
                className={`px-2 py-0.5 text-xs rounded ${docLang === "en" ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:bg-muted"}`}
              >EN</button>
              <button
                type="button"
                onClick={() => { setDocLang("fr"); toast({ title: "FR template coming soon", description: "LOI will be generated in English for now." }); }}
                className={`px-2 py-0.5 text-xs rounded ${docLang === "fr" ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:bg-muted"}`}
              >FR</button>
            </div>
            <Button variant="outline" size="sm" onClick={handleLoadDraft}>
              <FolderOpen className="w-4 h-4 mr-1" /> Load draft
            </Button>
            <Button variant="outline" size="sm" onClick={handleSaveDraft} className="border-amber-500/60 text-amber-700 hover:bg-amber-500/10">
              <Save className="w-4 h-4 mr-1" /> Save draft
            </Button>
          </div>
        </div>

        <div>
          <h1 className="text-3xl font-bold">LOI Generator</h1>
          <p className="text-muted-foreground mt-2">Create a Letter of Intent for business acquisition</p>
          {dealReference && (
            <p className="text-xs text-muted-foreground mt-1">Linked to deal: <span className="font-mono text-amber-700">{dealReference}</span></p>
          )}
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Buyer Information */}
            <Card>
              <CardHeader>
                <CardTitle>Buyer Information</CardTitle>
                <CardDescription>Details of the prospective buyer</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <FormField control={form.control} name="buyer.name" render={({ field }) => (
                  <FormItem><FormLabel>Full Name</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="buyer.company" render={({ field }) => (
                  <FormItem><FormLabel>Company</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="buyer.email" render={({ field }) => (
                  <FormItem><FormLabel>Email</FormLabel><FormControl><Input type="email" {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="buyer.phone" render={({ field }) => (
                  <FormItem><FormLabel>Phone</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="buyer.address" render={({ field }) => (
                  <FormItem><FormLabel>Address</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="buyer.city" render={({ field }) => (
                  <FormItem><FormLabel>City, State</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <div className="md:col-span-2">
                  <FormField control={form.control} name="buyer.signature" render={({ field }) => (
                    <FormItem><FormLabel>Buyer Signature</FormLabel><FormControl>
                      <SignaturePad signature={field.value || ''} onSignatureChange={field.onChange} />
                    </FormControl><FormMessage /></FormItem>
                  )} />
                </div>
              </CardContent>
            </Card>

            <Separator />

            {/* Seller Information */}
            <Card>
              <CardHeader>
                <CardTitle>Seller Information</CardTitle>
                <CardDescription>Details of the current owner</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <FormField control={form.control} name="seller.name" render={({ field }) => (
                  <FormItem><FormLabel>Full Name</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="seller.company" render={({ field }) => (
                  <FormItem><FormLabel>Company</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="seller.email" render={({ field }) => (
                  <FormItem><FormLabel>Email</FormLabel><FormControl><Input type="email" {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="seller.phone" render={({ field }) => (
                  <FormItem><FormLabel>Phone</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="seller.address" render={({ field }) => (
                  <FormItem><FormLabel>Address</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="seller.city" render={({ field }) => (
                  <FormItem><FormLabel>City, State</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                )} />
              </CardContent>
            </Card>

            <Separator />

            {/* Business Details */}
            <Card>
              <CardHeader>
                <CardTitle>Target Business</CardTitle>
                <CardDescription>Information about the business being acquired</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField control={form.control} name="businessName" render={({ field }) => (
                  <FormItem><FormLabel>Business Name</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="businessAddress" render={({ field }) => (
                  <FormItem><FormLabel>Business Address</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="businessDescription" render={({ field }) => (
                  <FormItem><FormLabel>Business Description</FormLabel><FormControl><Textarea {...field} rows={3} /></FormControl><FormMessage /></FormItem>
                )} />
              </CardContent>
            </Card>

            <Separator />

            {/* Purchase Terms */}
            <Card>
              <CardHeader>
                <CardTitle>Purchase Terms</CardTitle>
                <CardDescription>Financial terms of the proposed acquisition</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <FormField control={form.control} name="purchasePrice" render={({ field }) => (
                    <FormItem><FormLabel>Purchase Price</FormLabel><FormControl>
                      <Input type="number" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                    </FormControl><FormMessage /></FormItem>
                  )} />
                  <FormField control={form.control} name="currency" render={({ field }) => (
                    <FormItem><FormLabel>Currency</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="USD">USD</SelectItem>
                          <SelectItem value="CAD">CAD</SelectItem>
                          <SelectItem value="EUR">EUR</SelectItem>
                        </SelectContent>
                      </Select><FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="depositAmount" render={({ field }) => (
                    <FormItem><FormLabel>Deposit/Escrow</FormLabel><FormControl>
                      <Input type="number" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                    </FormControl><FormMessage /></FormItem>
                  )} />
                </div>

                <div className="grid gap-4 md:grid-cols-4">
                  <FormField control={form.control} name="paymentStructure.cashAtClosing" render={({ field }) => (
                    <FormItem><FormLabel>Cash at Closing</FormLabel><FormControl>
                      <Input type="number" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                    </FormControl><FormMessage /></FormItem>
                  )} />
                  <FormField control={form.control} name="paymentStructure.sellerFinancing" render={({ field }) => (
                    <FormItem><FormLabel>Seller Financing</FormLabel><FormControl>
                      <Input type="number" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                    </FormControl><FormMessage /></FormItem>
                  )} />
                  <FormField control={form.control} name="paymentStructure.earnout" render={({ field }) => (
                    <FormItem><FormLabel>Earnout</FormLabel><FormControl>
                      <Input type="number" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                    </FormControl><FormMessage /></FormItem>
                  )} />
                  <FormField control={form.control} name="paymentStructure.other" render={({ field }) => (
                    <FormItem><FormLabel>Other</FormLabel><FormControl>
                      <Input type="number" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                    </FormControl><FormMessage /></FormItem>
                  )} />
                </div>

                <FormField control={form.control} name="paymentStructure.description" render={({ field }) => (
                  <FormItem><FormLabel>Payment Notes (optional)</FormLabel><FormControl>
                    <Textarea {...field} placeholder="Additional notes about payment structure..." rows={2} />
                  </FormControl><FormMessage /></FormItem>
                )} />
              </CardContent>
            </Card>

            <Separator />

            {/* Timeline */}
            <Card>
              <CardHeader>
                <CardTitle>Timeline</CardTitle>
                <CardDescription>Key dates and periods</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-4">
                <FormField control={form.control} name="dueDiligenceDays" render={({ field }) => (
                  <FormItem><FormLabel>Due Diligence (days)</FormLabel><FormControl>
                    <Input type="number" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                  </FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="closingDays" render={({ field }) => (
                  <FormItem><FormLabel>Closing (days)</FormLabel><FormControl>
                    <Input type="number" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                  </FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="exclusivityDays" render={({ field }) => (
                  <FormItem><FormLabel>Exclusivity (days)</FormLabel><FormControl>
                    <Input type="number" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                  </FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="expirationDays" render={({ field }) => (
                  <FormItem><FormLabel>LOI Expires (days)</FormLabel><FormControl>
                    <Input type="number" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                  </FormControl><FormMessage /></FormItem>
                )} />
              </CardContent>
            </Card>

            <Separator />

            {/* Conditions */}
            <Card>
              <CardHeader>
                <CardTitle>Conditions Precedent</CardTitle>
                <CardDescription>Conditions that must be satisfied before closing</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-2">
                  {defaultConditions.map((condition) => (
                    <div key={condition} className="flex items-center space-x-2">
                      <Checkbox
                        checked={watchedConditions.includes(condition)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            form.setValue('conditions', [...watchedConditions, condition]);
                          } else {
                            form.setValue('conditions', watchedConditions.filter(c => c !== condition));
                          }
                        }}
                      />
                      <label className="text-sm">{condition}</label>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2">
                  <Input 
                    value={customCondition} 
                    onChange={e => setCustomCondition(e.target.value)} 
                    placeholder="Add custom condition..."
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustomCondition())}
                  />
                  <Button type="button" onClick={addCustomCondition} variant="outline" size="icon">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {watchedConditions.filter(c => !defaultConditions.includes(c)).map((condition, i) => (
                  <div key={i} className="flex items-center gap-2 bg-muted p-2 rounded">
                    <span className="flex-1 text-sm">{condition}</span>
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeCondition(watchedConditions.indexOf(condition))}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Separator />

            {/* Assets */}
            <Card>
              <CardHeader>
                <CardTitle>Assets Included</CardTitle>
                <CardDescription>Select assets included in the sale</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2 md:grid-cols-2">
                  {defaultAssetsIncluded.map((asset) => (
                    <div key={asset} className="flex items-center space-x-2">
                      <Checkbox
                        checked={watchedAssets.includes(asset)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            form.setValue('assetsIncluded', [...watchedAssets, asset]);
                          } else {
                            form.setValue('assetsIncluded', watchedAssets.filter(a => a !== asset));
                          }
                        }}
                      />
                      <label className="text-sm">{asset}</label>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Separator />

            {/* Additional Terms */}
            <Card>
              <CardHeader>
                <CardTitle>Additional Terms</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField control={form.control} name="employeeRetention" render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-3">
                      <div><FormLabel>Employee Retention</FormLabel><FormDescription>Offer employment to existing staff</FormDescription></div>
                      <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="sellerNonCompete" render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-3">
                      <div><FormLabel>Seller Non-Compete</FormLabel><FormDescription>Require non-compete agreement</FormDescription></div>
                      <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="arbitration" render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-3">
                      <div><FormLabel>Binding Arbitration</FormLabel><FormDescription>Use arbitration for disputes</FormDescription></div>
                      <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="bindingIntent" render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-3">
                      <div><FormLabel>Binding LOI</FormLabel><FormDescription>Make this LOI legally binding</FormDescription></div>
                      <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                    </FormItem>
                  )} />
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <FormField control={form.control} name="nonCompeteYears" render={({ field }) => (
                    <FormItem><FormLabel>Non-Compete Years</FormLabel><FormControl>
                      <Input type="number" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                    </FormControl><FormMessage /></FormItem>
                  )} />
                  <FormField control={form.control} name="nonCompeteRadius" render={({ field }) => (
                    <FormItem><FormLabel>Non-Compete Radius</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                  <FormField control={form.control} name="sellerTransitionPeriod" render={({ field }) => (
                    <FormItem><FormLabel>Transition Period (months)</FormLabel><FormControl>
                      <Input type="number" {...field} onChange={e => field.onChange(Number(e.target.value))} />
                    </FormControl><FormMessage /></FormItem>
                  )} />
                </div>

                <FormField control={form.control} name="applicableLaw" render={({ field }) => (
                  <FormItem><FormLabel>Governing Law</FormLabel><FormControl><Input {...field} /></FormControl><FormMessage /></FormItem>
                )} />
              </CardContent>
            </Card>

            <div className="flex justify-end gap-4">
              <Button type="button" variant="outline" onClick={() => form.reset()}>Reset</Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Generating..." : "Generate LOI"}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </DashboardLayout>
  );
}
